import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Target, Dumbbell, Clock, Lightbulb, RefreshCw, Play, BookOpen, Loader2, AlertCircle, Edit } from 'lucide-react';
import { ExerciseService } from '../lib/exerciseService';
import { Exercise } from '../types/dataModel';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../utils/youtubeUtils';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useTrainerLegacyExerciseRedirect } from '../features/trainer/shell/useTrainerLegacyExerciseRedirect';
import { PAGE_CHROME_LIGHT, PAGE_CHROME_LOADER_BRAND_GREEN } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

export const ExerciseDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const fromView = (location.state as { fromTrainerExercisesView?: 'library' | 'myExercises' } | undefined)?.fromTrainerExercisesView;

  const redirectingTrainer = useTrainerLegacyExerciseRedirect({
    viewExerciseId: profile?.role === 'trainer' ? id : undefined,
    exerciseViewMode: fromView || 'library',
  });

  const goBack = () => {
    if (profile?.role === 'trainer') {
      navigate('/trainer-dashboard', {
        state: { openLibraryExercises: true, exerciseViewMode: fromView || 'library' }
      });
    } else {
      navigate('/exercises');
    }
  };
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageStatusBar({
    background: loading ? PAGE_CHROME_LOADER_BRAND_GREEN : PAGE_CHROME_LIGHT,
  });

  useEffect(() => {
    if (redirectingTrainer) return;
    const fetchExercise = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const exerciseData = await ExerciseService.getExerciseById(id);
        if (exerciseData) {
          setExercise(exerciseData);
        } else {
          setError('Exercise not found');
        }
      } catch (err) {
        console.error('Error fetching exercise:', err);
        setError('Failed to load exercise details');
      } finally {
        setLoading(false);
      }
    };

    fetchExercise();
  }, [id, redirectingTrainer]);

  // Scroll to top when component mounts
  useEffect(() => {
    if (redirectingTrainer) return;
    window.scrollTo(0, 0);
  }, [redirectingTrainer]);

  if (redirectingTrainer) {
    return <LoadingSpinner fullScreen />;
  }

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error || !exercise) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || 'Exercise Not Found'}
          </h2>
          <button 
            onClick={goBack}
            className="btn-primary"
          >
            Back to Exercises
          </button>
        </div>
      </div>
    );
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'strength': return '💪';
      case 'cardio': return '❤️';
      case 'flexibility': return '🧘';
      case 'balance': return '⚖️';
      case 'sports': return '🏃';
      default: return '🏃';
    }
  };

  const handleEditExercise = () => {
    if (profile?.role === 'trainer') {
      navigate('/trainer-dashboard', {
        state: { openLibraryExercises: true, exerciseViewMode: fromView || 'library', editExerciseId: exercise.id }
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - back arrow + title + pill (same as member) */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center gap-3 p-4 md:p-6">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 break-words">{exercise.name}</h1>
            {exercise.muscleGroup && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                {exercise.muscleGroup}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Media section - video first (YouTube), then image, then non-YouTube video */}
      {(exercise.video || exercise.image) && (
        <div className="relative w-full">
          {exercise.video && isYouTubeUrl(exercise.video) ? (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={getYouTubeEmbedUrl(exercise.video) || ''}
                title={exercise.name}
                className="absolute top-0 left-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                frameBorder="0"
              />
            </div>
          ) : exercise.image ? (
            <div className="w-full h-64 overflow-hidden">
              <img
                src={exercise.image}
                alt={exercise.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : exercise.video ? (
            <div className="w-full aspect-video">
              <video src={exercise.video} controls className="w-full h-full rounded-lg">
                Your browser does not support the video tag.
              </video>
            </div>
          ) : null}
          {profile?.role === 'trainer' && (
            <button
              onClick={handleEditExercise}
              className="absolute top-3 left-3 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md flex items-center gap-2 shadow"
              aria-label="Edit exercise"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>
      )}

      {/* Category tag below media (same as member) */}
      {exercise.muscleGroup && (
        <div className="px-4 md:px-6 pt-4">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {exercise.muscleGroup}
          </span>
        </div>
      )}

      {/* Description - with book icon (same as member) */}
      {exercise.description && (
        <div className="p-4 md:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary-600" />
            Description
          </h3>
          <p className="text-gray-700 whitespace-pre-line">{exercise.description}</p>
        </div>
      )}

      {/* Action buttons - same layout as member (orange primary, gray secondary) */}
      <div className="p-4 md:p-6 pt-0">
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          {profile?.role === 'trainer' ? (
            <button
              onClick={handleEditExercise}
              className="flex-1 bg-[var(--action-primary)] hover:bg-[var(--action-primary-pressed)] text-[var(--ink)] font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Exercise
            </button>
          ) : (
            <button className="flex-1 bg-[var(--action-primary)] hover:bg-[var(--action-primary-pressed)] text-[var(--ink)] font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Play className="w-4 h-4" />
              Start Exercise
            </button>
          )}
          <button className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            Add to Workout
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetailPage;
